import React from "react";
import { format } from "date-fns";
import { id as indo } from "date-fns/locale";
import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonContent,
  IonFab,
  IonFabButton,
  IonFabList,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonModal,
  IonNote,
  IonTitle,
  IonToolbar,
  IonTextarea,
  IonSelect,
  IonSelectOption,
  IonDatetime,
  IonFooter,
  useIonToast,
} from "@ionic/react";
import {
  caretDownOutline,
  caretUpOutline,
  chevronUpCircleOutline,
  wallet,
  trashOutline,
} from "ionicons/icons";
import { useState } from "react";
import { useCollection } from "react-firebase-hooks/firestore";
import { useFirebase } from "../components/firebase-provider";
import PageLayout from "../components/layout";
import Loading from "../components/loading"

const Finance: React.FC = () => {
  const [toast] = useIonToast();
  const { firestore } = useFirebase();

  const date = new Date();
  const thisMonth = format(date, "yyyy-MM");
  const nextMonth = format(date.setMonth(date.getMonth() + 1), "yyyy-MM");

  const [snapshotsPemasukan, loadingPemasukan] = useCollection(
    firestore
      .collection("/data/keuangan/pemasukan")
      .where("waktu", ">=", thisMonth)
      .where("waktu", "<", nextMonth)
  );
  const [snapshotsPengeluaran, loadingPengeluaran] = useCollection(
    firestore
      .collection("/data/keuangan/pengeluaran")
      .where("waktu", ">=", thisMonth)
      .where("waktu", "<", nextMonth)
  );

  const listPengeluaran: any =
    snapshotsPengeluaran?.docs?.map((doc: any) => ({
      ...doc.data(),
      id: doc.id,
      jenis: "pengeluaran",
    })) || [];
  const listPemasukan: any =
    snapshotsPemasukan?.docs?.map((doc: any) => ({
      ...doc.data(),
      id: doc.id,
      jenis: "pemasukan",
    })) || [];
  const listKeuangan: any = [...listPengeluaran, ...listPemasukan].sort(
    (a: any, b: any) => {
      var dateA: any = new Date(a.waktu),
        dateB: any = new Date(b.waktu);
      return dateB - dateA;
    }
  );

  const totalPemasukan = listPemasukan
    .map((doc: any) => doc.jumlah)
    .reduce((a: any, b: any) => a + b, 0);

  const totalPengeluaran = listPengeluaran
    .map((doc: any) => doc.jumlah)
    .reduce((a: any, b: any) => a + b, 0);

  const [snapshotsRekening, loadingRekening] = useCollection(
    firestore.collection("/data/keuangan/akun")
  );

  const [modal, setModal] = useState({
    isActive: false,
    type: "",
  });

  // FORM DATA
  const initPemasukanFormData = {
    nama: "",
    akun: "",
    waktu: format(new Date(), "yyyy-MM-dd'T'HH:mmXXX"),
    jumlah: 0,
    catatan: "",
    timestamp: new Date(),
  };
  const [isSubmitting, setSubmitting] = useState(false);
  const [pemasukanForm, setPemasukanForm] = useState(initPemasukanFormData);
  const handlePemasukanForm = (el: any) =>
    setPemasukanForm({ ...pemasukanForm, [el.target.name]: el.detail.value });

  const initPengeluaranFormData = {
    nama: "",
    akun: "",
    kategori: "biaya produksi",
    waktu: format(new Date(), "yyyy-MM-dd'T'HH:mmXXX"),
    jumlah: 0,
    catatan: "",
    timestamp: new Date(),
  };
  const [pengeluaranForm, setPengeluaranForm] = useState(
    initPengeluaranFormData
  );
  const handlePengeluaranForm = (el: any) =>
    setPengeluaranForm({
      ...pengeluaranForm,
      [el.target.name]: el.detail.value,
    });

  const [showAll, setShowAll] = useState(false);
  return (
    <PageLayout
      title={`Keuangan ${format(new Date(), "MMMM yyyy", { locale: indo })}`}
    >
      {(loadingPemasukan || loadingPengeluaran || loadingRekening) && (
        <Loading />
      )}
      {snapshotsPemasukan && snapshotsPengeluaran && (
        <>
          <IonCard mode="ios">
            <IonCardHeader>
              <IonCardSubtitle>Pemasukan</IonCardSubtitle>
              <IonCardTitle color="success">
                {new Intl.NumberFormat("id-ID", {
                  style: "currency",
                  currency: "IDR",
                  maximumSignificantDigits: 10,
                }).format(Number(totalPemasukan - totalPengeluaran || 0))}
              </IonCardTitle>
            </IonCardHeader>

            <IonItem lines="none">
              <IonLabel>
                <p>Dana Masuk</p>
              </IonLabel>
              <IonLabel className="ion-text-end" color="warning" slot="end">
                +{" "}
                {new Intl.NumberFormat("id-ID", {
                  style: "currency",
                  currency: "IDR",
                  maximumSignificantDigits: 10,
                }).format(Number(totalPemasukan || 0))}
              </IonLabel>
            </IonItem>
            <IonItem lines="none">
              <IonLabel>
                <p>Dana Keluar</p>
              </IonLabel>
              <IonLabel className="ion-text-end" color="danger" slot="end">
                -{" "}
                {new Intl.NumberFormat("id-ID", {
                  style: "currency",
                  currency: "IDR",
                  maximumSignificantDigits: 10,
                }).format(Number(totalPengeluaran || 0))}
              </IonLabel>
            </IonItem>
          </IonCard>

          <IonList>
            <IonListHeader>
              <h3>Catatan Keuangan</h3>
            </IonListHeader>

            {(showAll ? listKeuangan : listKeuangan.slice(0, 10)).map(
              (el: any) => (
                <React.Fragment key={el.id}>
                  <IonItem lines="full">
                    <IonIcon
                      color={el.jenis === "pemasukan" ? "success" : "danger"}
                      icon={
                        el.jenis === "pemasukan"
                          ? caretUpOutline
                          : caretDownOutline
                      }
                      slot="start"
                      size="small"
                    />
                    <IonLabel>
                      <p>{el.nama}</p>
                      {el.jenis === "pemasukan" ? "+ " : "- "}
                      {new Intl.NumberFormat("id-ID", {
                        style: "currency",
                        currency: "IDR",
                        maximumSignificantDigits: 10,
                      }).format(Number(el.jumlah || 0))}
                    </IonLabel>

                    {(format(new Date(el.waktu), "dd MM") !==
                      format(new Date(), "dd MM") ||
                      el.catatan === "Di input oleh sistem") && (
                      <IonNote slot="end">
                        {format(new Date(el.waktu), "dd MMMM yyyy", {
                          locale: indo,
                        })}
                      </IonNote>
                    )}

                    {format(new Date(el.waktu), "dd MM") ===
                      format(new Date(), "dd MM") &&
                      el.catatan !== "Di input oleh sistem" && (
                        <IonButton
                          fill="clear"
                          slot="end"
                          color="danger"
                          onClick={async () => {
                            var kasAkunObj = Object.fromEntries(
                              new Map(
                                snapshotsRekening?.docs?.map((doc) => [
                                  doc.id,
                                  doc.data(),
                                ])
                              )
                            );

                            if (el.jenis === "pemasukan") {
                              const updateRekening = firestore
                                .collection("/data/keuangan/akun")
                                .doc(el.akun)
                                .update({
                                  saldo:
                                    Number(kasAkunObj[el.akun]?.saldo) -
                                    Number(el.jumlah),
                                });
                              const del = firestore
                                .collection("/data/keuangan/pemasukan")
                                .doc(el.id)
                                .delete();

                              await Promise.all([updateRekening, del])
                                .then(() => {
                                  toast({
                                    message: "Pemasukan berhasil dihapus!",
                                    duration: 1000,
                                    color: "success",
                                    position: "top",
                                  });
                                })
                                .catch(() => {
                                  toast({
                                    message: "Terjadi kesalahan jaringan!",
                                    duration: 1000,
                                    color: "danger",
                                    position: "top",
                                  });
                                });
                            } else {
                              const updateRekening = firestore
                                .collection("/data/keuangan/akun")
                                .doc(el.akun)
                                .update({
                                  saldo:
                                    Number(kasAkunObj[el.akun]?.saldo) +
                                    Number(el.jumlah),
                                });
                              const del = firestore
                                .collection("/data/keuangan/pengeluaran")
                                .doc(el.id)
                                .delete();

                              await Promise.all([updateRekening, del])
                                .then(() => {
                                  toast({
                                    message: "Pengeluaran berhasil dihapus!",
                                    duration: 1000,
                                    color: "success",
                                    position: "top",
                                  });
                                })
                                .catch(() => {
                                  toast({
                                    message: "Terjadi kesalahan jaringan!",
                                    duration: 1000,
                                    color: "danger",
                                    position: "top",
                                  });
                                });
                            }
                          }}
                        >
                          <IonIcon icon={trashOutline} />
                        </IonButton>
                      )}
                  </IonItem>
                </React.Fragment>
              )
            )}
            <section>
              {!showAll && listKeuangan.length > 10 && (
                <IonButton
                  fill="clear"
                  onClick={() => setShowAll(true)}
                  expand="block"
                  mode="ios"
                >
                  Lihat Semua
                </IonButton>
              )}
            </section>
          </IonList>

          <IonFab vertical="bottom" horizontal="end" slot="fixed">
            <IonFabButton color="secondary">
              <IonIcon icon={chevronUpCircleOutline} />
            </IonFabButton>
            <IonFabList side="top">
              <IonFabButton routerLink="/app/finance/account">
                <IonIcon icon={wallet} />
              </IonFabButton>

              <IonFabButton
                onClick={() =>
                  setModal({ isActive: true, type: "expenditure" })
                }
              >
                <IonIcon color="danger" icon={caretDownOutline} />
              </IonFabButton>

              <IonFabButton
                onClick={() => setModal({ isActive: true, type: "income" })}
              >
                <IonIcon color="success" icon={caretUpOutline} />
              </IonFabButton>
            </IonFabList>
          </IonFab>

          {/* MODAL */}
          {/* Pemasukan */}
          <IonModal
            isOpen={modal.isActive && modal.type === "income"}
            onDidDismiss={() => setModal({ isActive: false, type: "" })}
          >
            <IonHeader>
              <IonToolbar>
                <IonTitle>Input Pemasukan</IonTitle>
                <IonButtons slot="end">
                  <IonButton
                    onClick={() => setModal({ isActive: false, type: "" })}
                  >
                    Tutup
                  </IonButton>
                </IonButtons>
              </IonToolbar>
            </IonHeader>

            <IonContent>
              <IonItem lines="full">
                <IonLabel position="stacked">Sumber Pemasukan</IonLabel>
                <IonInput
                  name="nama"
                  disabled={isSubmitting}
                  value={pemasukanForm.nama}
                  onIonChange={handlePemasukanForm}
                />
              </IonItem>

              <IonItem lines="full">
                <IonLabel position="stacked">Rekening Tujuan</IonLabel>
                <IonSelect
                  name="akun"
                  disabled={isSubmitting}
                  onIonChange={handlePemasukanForm}
                  value={pemasukanForm.akun}
                >
                  {snapshotsRekening?.docs?.map((doc) => (
                    <React.Fragment key={doc.id}>
                      <IonSelectOption value={doc.id}>
                        {doc?.data()?.jenis === "cash"
                          ? `Kas ${doc?.data()?.nama}`
                          : `${doc?.data()?.bank} (${doc?.data()?.nama})`}
                      </IonSelectOption>
                    </React.Fragment>
                  ))}
                </IonSelect>
              </IonItem>

              <IonItem lines="full">
                <IonLabel position="stacked">Waktu</IonLabel>
                <IonDatetime
                  name="waktu"
                  onIonChange={handlePemasukanForm}
                  disabled={isSubmitting}
                  value={pemasukanForm.waktu}
                  monthShortNames="Jan, Feb, Mar, Apr, Mei, Jun, Jul, Agu, Sep, Okt, Nov, Des"
                />
              </IonItem>

              <IonItem lines="full">
                <IonLabel position="stacked">Jumlah</IonLabel>
                <IonInput
                  type="number"
                  name="jumlah"
                  disabled={isSubmitting}
                  value={pemasukanForm.jumlah}
                  onIonChange={(el) =>
                    setPemasukanForm({
                      ...pemasukanForm,
                      jumlah: Number(el.detail.value),
                    })
                  }
                />
              </IonItem>

              <IonItem lines="full">
                <IonLabel position="stacked">Catatan Tambahan</IonLabel>
                <IonTextarea
                  disabled={isSubmitting}
                  name="catatan"
                  value={pemasukanForm.catatan}
                />
              </IonItem>
            </IonContent>
            <IonFooter>
              <IonToolbar>
                <IonButton
                  className="ion-margin"
                  fill="outline"
                  disabled={isSubmitting}
                  onClick={async () => {
                    setSubmitting(true);
                    var kasAkunObj = Object.fromEntries(
                      new Map(
                        snapshotsRekening?.docs?.map((doc) => [
                          doc.id,
                          doc.data(),
                        ])
                      )
                    );

                    const updateRekening = firestore
                      .collection("/data/keuangan/akun")
                      .doc(pemasukanForm.akun)
                      .update({
                        saldo:
                          Number(kasAkunObj[pemasukanForm.akun].saldo) +
                          Number(pemasukanForm.jumlah),
                      });
                    const inputPemasukan = firestore
                      .collection("/data/keuangan/pemasukan")
                      .add(pemasukanForm);

                    await Promise.all([updateRekening, inputPemasukan])
                      .then(() => {
                        setModal({ isActive: false, type: "" });
                        toast({
                          message: "Pemasukan berhasil ditambahkan!",
                          duration: 3000,
                          color: "success",
                          position: "top",
                        });
                        setPemasukanForm(initPemasukanFormData);
                      })
                      .catch(() => {
                        toast({
                          message: "Terjadi kesalahan jaringan!",
                          duration: 3000,
                          color: "danger",
                          position: "top",
                        });
                      });

                    setSubmitting(false);
                  }}
                >
                  Input Pemasukan
                </IonButton>
              </IonToolbar>
            </IonFooter>
          </IonModal>

          {/* Pengeluaran */}
          <IonModal
            isOpen={modal.isActive && modal.type === "expenditure"}
            onDidDismiss={() => setModal({ isActive: false, type: "" })}
          >
            <IonHeader>
              <IonToolbar>
                <IonTitle>Input Pengeluaran</IonTitle>
                <IonButtons slot="end">
                  <IonButton
                    onClick={() => setModal({ isActive: false, type: "" })}
                  >
                    Tutup
                  </IonButton>
                </IonButtons>
              </IonToolbar>
            </IonHeader>

            <IonContent>
            <IonItem lines="full">
                <IonLabel position="stacked">Kategori Pengeluaran</IonLabel>
                <IonSelect
                  name="akun"
                  disabled={isSubmitting}
                  onIonChange={handlePengeluaranForm}
                  value={pengeluaranForm.kategori}
                >
                  <IonSelectOption value="biaya produksi">
                    Biaya Produksi
                  </IonSelectOption>
                  <IonSelectOption value="biaya operasional">
                    Biaya Operasional
                  </IonSelectOption>
                  <IonSelectOption value="biaya lainnya">
                    Biaya Lainnya
                  </IonSelectOption>
                </IonSelect>
              </IonItem>

              <IonItem lines="full">
                <IonLabel position="stacked">Jenis Pengeluaran</IonLabel>
                <IonInput
                  name="nama"
                  disabled={isSubmitting}
                  value={pengeluaranForm.nama}
                  onIonChange={handlePengeluaranForm}
                />
              </IonItem>

              <IonItem lines="full">
                <IonLabel position="stacked">Dari Rekening</IonLabel>
                <IonSelect
                  disabled={isSubmitting}
                  // interface="action-sheet"
                  name="akun"
                  onIonChange={handlePengeluaranForm}
                  value={pengeluaranForm.akun}
                >
                  {snapshotsRekening?.docs?.map((doc) => (
                    <React.Fragment key={doc.id}>
                      <IonSelectOption value={doc.id}>
                        {doc?.data()?.jenis === "cash"
                          ? `Kas ${doc?.data()?.nama} - ${new Intl.NumberFormat(
                              "id-ID",
                              {
                                style: "currency",
                                currency: "IDR",
                                maximumSignificantDigits: 10,
                              }
                            ).format(Number(doc?.data()?.saldo || 0))}`
                          : `${doc?.data()?.bank} (${
                              doc?.data()?.nama
                            }) - ${new Intl.NumberFormat("id-ID", {
                              style: "currency",
                              currency: "IDR",
                              maximumSignificantDigits: 10,
                            }).format(Number(doc?.data()?.saldo || 0))}`}
                      </IonSelectOption>
                    </React.Fragment>
                  ))}
                </IonSelect>
              </IonItem>

              <IonItem lines="full">
                <IonLabel position="stacked">Waktu</IonLabel>
                <IonDatetime
                  disabled={isSubmitting}
                  name="waktu"
                  onIonChange={handlePengeluaranForm}
                  value={pengeluaranForm.waktu}
                  monthShortNames="Jan, Feb, Mar, Apr, Mei, Jun, Jul, Agu, Sep, Okt, Nov, Des"
                />
              </IonItem>

              <IonItem lines="full">
                <IonLabel position="stacked">Jumlah</IonLabel>
                <IonInput
                  type="number"
                  disabled={isSubmitting}
                  name="jumlah"
                  value={pengeluaranForm.jumlah}
                  onIonChange={(el) =>
                    setPengeluaranForm({
                      ...pengeluaranForm,
                      jumlah: Number(el.detail.value),
                    })
                  }
                />
              </IonItem>

              <IonItem lines="full">
                <IonLabel position="stacked">Catatan Tambahan</IonLabel>
                <IonTextarea name="catatan" value={pengeluaranForm.catatan} />
              </IonItem>
            </IonContent>
            <IonFooter>
              <IonToolbar>
                <IonButton
                  fill="outline"
                  className="ion-margin"
                  disabled={isSubmitting}
                  onClick={async () => {
                    setSubmitting(true);
                    var kasAkunObj = Object.fromEntries(
                      new Map(
                        snapshotsRekening?.docs?.map((doc) => [
                          doc.id,
                          doc.data(),
                        ])
                      )
                    );

                    const updateRekening = firestore
                      .collection("/data/keuangan/akun")
                      .doc(pengeluaranForm.akun)
                      .update({
                        saldo:
                          Number(kasAkunObj[pengeluaranForm.akun].saldo) -
                          Number(pengeluaranForm.jumlah),
                      });
                    const inputPengeluaran = firestore
                      .collection("/data/keuangan/pengeluaran")
                      .add(pengeluaranForm);

                    await Promise.all([updateRekening, inputPengeluaran])
                      .then(() => {
                        setModal({ isActive: false, type: "" });
                        toast({
                          message: "Pengeluaran berhasil ditambahkan!",
                          duration: 3000,
                          color: "success",
                          position: "top",
                        });
                        setPemasukanForm(initPengeluaranFormData);
                      })
                      .catch(() => {
                        toast({
                          message: "Terjadi kesalahan jaringan!",
                          duration: 3000,
                          color: "danger",
                          position: "top",
                        });
                      });
                    setSubmitting(false);
                  }}
                >
                  Input Pengeluaran
                </IonButton>
              </IonToolbar>
            </IonFooter>
          </IonModal>
        </>
      )}
    </PageLayout>
  );
};

export default Finance;
